/**
 * Promotion List dashboard — pinned candidate crosses.
 *
 * Displays all crosses the user has saved from the Combined Selection
 * Table (heterosis + GCA/SCA grid). Lets them remove individual
 * candidates and generate a one-page DOCX breeding plan from the
 * pinned set.
 *
 * Niranjan, Feb 2026 — Phase C dashboard.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import {
  Pin, Trash2, FileText, Loader2, Sparkles,
  ArrowLeft, Calendar, RefreshCcw,
} from 'lucide-react';

const fmt = (v, d = 2) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return Number(v).toFixed(d);
};

const fmtPct = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`;
};

export default function PromotionList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [aiMode, setAiMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/promotion-list`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load promotion list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const removeOne = async (cross, analysisId) => {
    if (!window.confirm(`Remove ${cross} from your promotion list?`)) return;
    try {
      const params = { cross };
      if (analysisId) params.analysis_id = analysisId;
      await axios.delete(`${API}/promotion-list`, { params, withCredentials: true });
      toast.success(`Removed ${cross}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove.');
    }
  };

  const generatePlan = async () => {
    if (!items.length) {
      toast.error('Pin at least one candidate first.');
      return;
    }
    setGeneratingPlan(true);
    try {
      const response = await axios.post(
        `${API}/promotion-list/breeding-plan`,
        {},
        {
          params: aiMode ? { ai_mode: true } : {},
          responseType: 'blob',
          withCredentials: true,
          // AI mode fans out concurrent LLM calls — 90 s ceiling lets
          // each Sonnet call (~30 s with retries) complete in parallel.
          timeout: aiMode ? 90000 : 15000,
        },
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `breeding_plan${aiMode ? '_ai' : ''}_${new Date().toISOString().slice(0, 10)}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(
        aiMode
          ? 'AI-enhanced breeding plan generated and downloaded.'
          : 'Breeding plan generated and downloaded.',
      );
    } catch (e) {
      const detail = e?.response?.data
        ? (typeof e.response.data === 'string' ? e.response.data : await e.response.data.text())
        : 'Could not generate breeding plan.';
      toast.error(detail);
    } finally {
      setGeneratingPlan(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFCFA] py-8 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto" data-testid="promotion-list-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-[12px] text-[#6B8F71] hover:text-[#1F4E2E] flex items-center gap-1 mb-3 transition-colors"
              data-testid="promotion-back"
            >
              <ArrowLeft size={12} /> Back
            </button>
            <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-[#1F4E2E] flex items-center gap-2 mb-1">
              <Pin size={22} className="text-[#7C3AED]" />
              Promotion List
            </h1>
            <p className="text-[13px] text-[#6B8F71] max-w-2xl">
              Cross candidates you&apos;ve pinned from your heterosis &amp; combining-ability analyses.
              Generate a one-page DOCX breeding plan that proposes F2/F3 advancement schedules
              based on each cross&apos;s gene-action verdict.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {/* AI mode toggle — Claude Sonnet 4.5 rationale rewrite */}
            <label
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer transition-all"
              style={{
                borderColor: aiMode ? '#7C3AED' : '#E0EDE0',
                background: aiMode ? '#FAF8FF' : 'white',
              }}
              data-testid="ai-mode-toggle"
            >
              <input
                type="checkbox"
                checked={aiMode}
                onChange={(e) => setAiMode(e.target.checked)}
                className="cursor-pointer"
                style={{ accentColor: '#7C3AED' }}
              />
              <Sparkles size={12} className={aiMode ? 'text-[#7C3AED]' : 'text-[#6B8F71]'} />
              <span
                className="text-[12px] font-bold"
                style={{ color: aiMode ? '#5B21B6' : '#3C4043' }}
              >
                AI rationale
              </span>
              <span className="text-[10px] text-[#6B8F71]">(Claude Sonnet 4.5)</span>
            </label>

            <button
              onClick={load}
              disabled={loading}
              className="px-3 py-2 rounded-md border border-[#E0EDE0] text-[12px] font-medium text-[#3C4043] hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-50"
              data-testid="promotion-refresh"
            >
              <RefreshCcw size={12} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={generatePlan}
              disabled={generatingPlan || !items.length}
              data-testid="generate-breeding-plan"
              className="px-4 py-2 rounded-md text-[12px] font-bold text-white shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
                letterSpacing: '0.02em',
              }}
            >
              {generatingPlan ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Sparkles size={12} />
              )}
              {generatingPlan
                ? aiMode ? 'Generating with AI…' : 'Generating…'
                : aiMode ? 'Generate AI Plan (DOCX)' : 'Generate Breeding Plan (DOCX)'}
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#6B8F71]">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading…
          </div>
        ) : !items.length ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-3" data-testid="promotion-items">
            {items.map((it, i) => (
              <CandidateRow
                key={`${it.cross}-${it.analysis_id || i}`}
                item={it}
                onRemove={() => removeOne(it.cross, it.analysis_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border-2 border-dashed border-[#E0EDE0] bg-white py-14 px-6 text-center" data-testid="promotion-empty">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F4FAF5] text-[#7C3AED] mb-4">
        <Pin size={20} />
      </div>
      <h3 className="text-[15px] font-bold text-[#1F4E2E] mb-1">No pinned candidates yet</h3>
      <p className="text-[12px] text-[#6B8F71] max-w-md mx-auto mb-4">
        Run a Diallel or Line × Tester analysis. In the Combined Selection Table,
        click <strong className="text-[#7C3AED]">Pin</strong> next to any cross to save it here.
      </p>
    </div>
  );
}

function CandidateRow({ item, onRemove }) {
  const score = item.top_score;
  const date = item.pinned_at ? new Date(item.pinned_at) : null;
  return (
    <div
      className="rounded-xl border border-[#E0EDE0] bg-white p-4 hover:border-[#7C3AED] transition-colors"
      data-testid={`promotion-row-${item.cross}`}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="shrink-0 flex flex-col items-center justify-center w-14 h-14 rounded-lg" style={{
          background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
          color: 'white',
        }}>
          <span className="text-[18px] font-bold leading-none">{score ?? '—'}</span>
          <span className="text-[8.5px] uppercase tracking-wider opacity-70 mt-0.5">score</span>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-baseline gap-2 flex-wrap mb-2">
            <h3 className="font-mono text-[16px] font-bold text-[#1F4E2E]">{item.cross}</h3>
            {item.trait && (
              <span className="text-[11px] text-[#6B8F71] italic">trait: {item.trait}</span>
            )}
            {item.analysis_type && (
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-[#F4FAF5] text-[#0B5132] border border-[#E0EDE0]">
                {item.analysis_type.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-1 text-[11px]">
            <Metric label="F1 mean" v={item.f1_mean} fn={fmt} />
            <Metric label="GCA(P1+P2)" v={item.gca_sum} fn={fmt} />
            <Metric label="SCA" v={item.sca} fn={fmt} />
            <Metric label="MPH" v={item.mph_pct} fn={fmtPct} />
            <Metric label="BPH" v={item.bph_pct} fn={fmtPct} />
            <Metric label="SH" v={item.sh_pct} fn={fmtPct} />
          </div>
          {date && (
            <div className="text-[10px] text-[#6B8F71] mt-2 flex items-center gap-1">
              <Calendar size={10} />
              Pinned {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
        <button
          onClick={onRemove}
          className="text-[11px] font-medium text-[#9F1239] hover:bg-[#FDF1F4] px-3 py-1.5 rounded-md transition-colors flex items-center gap-1"
          data-testid={`promotion-remove-${item.cross}`}
        >
          <Trash2 size={12} /> Remove
        </button>
      </div>
    </div>
  );
}

function Metric({ label, v, fn }) {
  return (
    <div>
      <div className="text-[#6B8F71] uppercase tracking-wider font-bold text-[9.5px] mb-0.5">{label}</div>
      <div className="font-mono text-[12px] text-[#1F4E2E] font-semibold">{fn(v)}</div>
    </div>
  );
}
